import { HttpErrorResponse } from '@angular/common/http';
import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  IndicatorType,
  ProductionEvent,
  ProductionEventAction,
  ScheduleButtonType,
} from '@groundworks/ngx-groundworks-ui';
import { SwaggerException } from '../sdk/sdk';
import { formatLocalIsoDate, formatUtcIsoDate } from '../utils/iso-date';
import { BranchService } from './branch.service';
import { CalendarService } from './calendar.service';
import { CrewScheduleStore } from './crew-schedule-store.service';
import { ModalService } from './modal.service';

@Injectable({ providedIn: 'root' })
export class InstallDateManagementService {
  private readonly calendarService = inject(CalendarService);
  private readonly modalService = inject(ModalService);
  private readonly store = inject(CrewScheduleStore);
  private readonly branchService = inject(BranchService);

  handleEventAction(action: ProductionEventAction, destroyRef: DestroyRef): void {
    if (action.action === 'add') {
      this.addInstallDate(action.event, destroyRef);
    } else {
      this.removeInstallDate(action.event, destroyRef);
    }
  }

  private addInstallDate(event: ProductionEvent, destroyRef: DestroyRef): void {
    const eventKey = event.id;
    const foremanUserId = this.store.getForemanId(eventKey);
    const projectId = this.store.getProjectId(eventKey);
    const lastDate = this.store.getLastDate(eventKey);
    if (!foremanUserId || !projectId || !lastDate) {
      console.warn('addInstallDate: missing required state for event', eventKey, { foremanUserId, projectId, lastDate });
      this.modalService.openErrorModal(
        'Unable to Add Install Date',
        'The calendar data may be stale. Please refresh the page and try again.');
      return;
    }

    const nextDate = new Date(lastDate);
    nextDate.setDate(nextDate.getDate() + 1);

    const branchId = this.branchService.getCurrentSelectedBranch()?.id;

    this.calendarService.addInstallDate(projectId, foremanUserId, nextDate, branchId)
      .pipe(takeUntilDestroyed(destroyRef))
      .subscribe({
        next: (response) => {
          // The API returns a DateOnly as a string (e.g. "2026-03-04") via http.post.
          // When the SDK deserializes it, it becomes a UTC-midnight Date object.
          const returnedDate = typeof response.date === 'string'
            ? response.date
            : formatUtcIsoDate(response.date);
          const requestedDate = formatLocalIsoDate(nextDate);

          if (returnedDate !== requestedDate) {
            this.store.loadCrewSchedules(foremanUserId);
            return;
          }

          const lastDateIsSunday = lastDate.getDay() === 0;
          this.store.setLastDate(eventKey, nextDate);
          this.store.pushInstallDateId(eventKey, response.id);
          this.addDateToCrewGroup(foremanUserId, nextDate, event, response.dailyInstallAmount, lastDateIsSunday);
        },
        error: (err: unknown) => {
          console.error('Failed to add install date:', err);
          const status = err instanceof HttpErrorResponse ? err.status
            : SwaggerException.isSwaggerException(err) ? err.status
            : undefined;
          const isBadRequest = status === 400;
          const isNoAvailability = status === 409;
          let message: string;
          if (isNoAvailability) {
            message = 'The foreman has no available working days. Please add availability before scheduling install dates.';
          } else if (isBadRequest) {
            message = this.serverRefusalMessage(err)
              ?? 'Install dates cannot be added to a cancelled or descoped project.';
          } else {
            message = 'An unexpected error occurred while adding the install date. Please try again.';
          }
          const modalRef = this.modalService.openErrorModal('Unable to Add Install Date', message);
          void modalRef.result.then(
            () => { this.store.loadCrewSchedules(foremanUserId); },
            () => { this.store.loadCrewSchedules(foremanUserId); },
          );
        },
      });
  }

  /**
   * The server's own wording for a refused save, or null when it sent none.
   *
   * The server already distinguishes the two refusals in prose —
   * `CancelledProjectScheduleException.ForDescopedFutureAddition` says "descoped", `ForFutureAddition`
   * says "cancelled" — but both travel under a single `ErrorCode`, so the code cannot tell them
   * apart and only the sentence can. Hardcoding "cancelled" here therefore told a user whose project
   * was DESCOPED the wrong reason, and discarded a message the server had already written correctly.
   *
   * `detail` is where a DomainException's ProblemDetails carries it; `message` covers the shape other
   * responses use. Mirrors `ScheduleProjectPanelComponent.cancelledScheduleMessage`, which was
   * already doing this — this service was the second, unfixed path onto the same server guard.
   */
  private serverRefusalMessage(err: unknown): string | null {
    const body = err instanceof HttpErrorResponse
      && typeof err.error === 'object' && err.error !== null
      ? (err.error as Record<string, unknown>)
      : null;
    if (!body) return null;

    for (const key of ['detail', 'message']) {
      const value = body[key];
      if (typeof value === 'string' && value.trim()) return value.trim();
    }
    return null;
  }

  // When the previous last date falls on Sunday, the new date starts a new week — strip last-day indicators instead of extending span
  private addDateToCrewGroup(foremanUserId: string, date: Date, sourceEvent: ProductionEvent, dailyInstallAmount: number | undefined, stripLastDayIndicators: boolean): void {
    const crewGroupIndex = this.store.findCrewGroupIndex(foremanUserId);
    if (crewGroupIndex === -1) { this.store.loadCrewSchedules(foremanUserId); return; }
    const original = this.store.getCrewGroupAt(crewGroupIndex);
    if (!original) { this.store.loadCrewSchedules(foremanUserId); return; }

    for (let dayIdx = 0; dayIdx < original.schedule.length; dayIdx++) {
      const day = original.schedule[dayIdx];
      const eventIndex = day.events.findIndex((e) => e.id === sourceEvent.id);
      if (eventIndex === -1) continue;

      const existing = day.events[eventIndex];
      const updatedEvents = [...day.events];

      if (stripLastDayIndicators) {
        updatedEvents[eventIndex] = {
          ...existing,
          indicators: existing.indicators?.filter(
            (i) => i.type !== IndicatorType.LAST_DAY && i.type !== IndicatorType.JOB_LAST_DAY
          ),
        };
      } else {
        const newSpanDays = (existing.spanDays ?? 1) + 1;
        const footerAmount = CrewScheduleStore.formatCurrency(dailyInstallAmount ?? 0);
        updatedEvents[eventIndex] = {
          ...existing,
          spanDays: newSpanDays,
          footer: Array.from({ length: newSpanDays }, () => ({ text: footerAmount })),
        };
      }

      let updatedSchedule = [...original.schedule];
      updatedSchedule[dayIdx] = { ...day, events: updatedEvents };

      if (!stripLastDayIndicators) {
        const dayExists = updatedSchedule.some((ds) => ds.date.getTime() === date.getTime());
        if (!dayExists) {
          updatedSchedule = [
            ...updatedSchedule,
            { date, events: [], buttonType: ScheduleButtonType.ADD },
          ].sort((a, b) => a.date.getTime() - b.date.getTime());
        }
      }

      this.store.setCrewGroupAt(crewGroupIndex, { ...original, schedule: updatedSchedule });
      return;
    }

    console.warn('addDateToCrewGroup: event not found in crew schedule, forcing reload', sourceEvent.id);
    this.store.loadCrewSchedules(foremanUserId);
  }

  private removeInstallDate(event: ProductionEvent, destroyRef: DestroyRef): void {
    const eventKey = event.id;
    const installDateIds = this.store.getInstallDateIds(eventKey);
    const foremanUserId = this.store.getForemanId(eventKey);
    const projectId = this.store.getProjectId(eventKey);
    const lastDate = this.store.getLastDate(eventKey);
    if (!installDateIds?.length || !foremanUserId || !projectId || !lastDate) {
      console.warn('removeInstallDate: missing required state for event', eventKey, { installDateIds, foremanUserId, projectId, lastDate });
      this.modalService.openErrorModal(
        'Unable to Remove Install Date',
        'The calendar data may be stale. Please refresh the page and try again.');
      return;
    }

    const installDateId = installDateIds[installDateIds.length - 1];
    const currentSpan = event.spanDays ?? 1;

    this.calendarService.removeInstallDate(projectId, installDateId)
      .pipe(takeUntilDestroyed(destroyRef))
      .subscribe({
        next: (response) => {
          if (currentSpan <= 1) {
            this.store.loadCrewSchedules(foremanUserId);
          } else {
            const previousDate = new Date(lastDate);
            previousDate.setDate(previousDate.getDate() - 1);
            this.store.setLastDate(eventKey, previousDate);
            this.store.popInstallDateId(eventKey);
            this.removeDateFromCrewGroup(foremanUserId, event, response.dailyInstallAmount);
          }
        },
        error: (err: unknown) => {
          console.error('Failed to remove install date:', err);
          const isBadRequest = err instanceof HttpErrorResponse && err.status === 400;
          const modalRef = this.modalService.openErrorModal(
            'Unable to Remove Install Date',
            isBadRequest
              ? this.serverRefusalMessage(err)
                ?? 'Install dates cannot be removed from a cancelled or descoped project.'
              : 'An unexpected error occurred while removing the install date. Please try again.');
          void modalRef.result.then(
            () => { this.store.loadCrewSchedules(foremanUserId); },
            () => { this.store.loadCrewSchedules(foremanUserId); },
          );
        },
      });
  }

  private removeDateFromCrewGroup(foremanUserId: string, sourceEvent: ProductionEvent, dailyInstallAmount?: number): void {
    const crewGroupIndex = this.store.findCrewGroupIndex(foremanUserId);
    if (crewGroupIndex === -1) { this.store.loadCrewSchedules(foremanUserId); return; }
    const original = this.store.getCrewGroupAt(crewGroupIndex);
    if (!original) { this.store.loadCrewSchedules(foremanUserId); return; }

    for (let dayIdx = 0; dayIdx < original.schedule.length; dayIdx++) {
      const day = original.schedule[dayIdx];
      const eventIndex = day.events.findIndex((e) => e.id === sourceEvent.id);
      if (eventIndex === -1) continue;

      const existing = day.events[eventIndex];
      const currentSpan = existing.spanDays ?? 1;
      if (currentSpan <= 1) {
        console.warn('removeDateFromCrewGroup: event at minimum span, forcing reload', sourceEvent.id);
        this.store.loadCrewSchedules(foremanUserId);
        return;
      }

      const newSpanDays = currentSpan - 1;
      const footerAmount = CrewScheduleStore.formatCurrency(dailyInstallAmount ?? 0);
      const updatedEvents = [...day.events];
      updatedEvents[eventIndex] = {
        ...existing,
        spanDays: newSpanDays > 1 ? newSpanDays : undefined,
        footer: Array.from({ length: newSpanDays }, () => ({ text: footerAmount })),
      };

      const updatedSchedule = [...original.schedule];
      updatedSchedule[dayIdx] = { ...day, events: updatedEvents };
      this.store.setCrewGroupAt(crewGroupIndex, { ...original, schedule: updatedSchedule });
      return;
    }

    console.warn('removeDateFromCrewGroup: event not found in crew schedule, forcing reload', sourceEvent.id);
    this.store.loadCrewSchedules(foremanUserId);
  }
}
